import React, { useRef, useEffect } from "react";

interface CompatibilityScrollableHeaderProps {
  years: string[];
  activeYears: string[];
  onYearSelect: (year: string) => void;
  scrollPosition?: number;
  onScroll?: (scrollLeft: number) => void;
}

const BUTTON_WIDTH = 60;
const SPACING = 4;
const PADDING = 16;

const CompatibilityScrollableHeader: React.FC<CompatibilityScrollableHeaderProps> = ({ years, activeYears, onYearSelect }) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleYearClick = (year: string) => {
    onYearSelect(year);
  };

  // Scroll to make the first active year visible when activeYears changes
  useEffect(() => {
    if (activeYears.length > 0 && scrollContainerRef.current) {
      const firstActiveYear = activeYears[0];
      const yearIndex = years.indexOf(firstActiveYear);

      if (yearIndex !== -1) {
        // Calculate approximate scroll position
        const scrollLeft = yearIndex * (BUTTON_WIDTH + SPACING) - PADDING;

        scrollContainerRef.current.scrollTo({
          left: Math.max(0, scrollLeft),
          behavior: "smooth",
        });
      }
    }
  }, [activeYears, years]);

  return (
    <div className="w-full border-b border-gray-200 bg-white">
      <div
        ref={scrollContainerRef}
        className="flex overflow-x-auto scrollbar-hide"
        style={{
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        <div className="flex space-x-1 px-4 py-2 min-w-max">
          {years.map((year) => (
            <button
              key={year}
              onClick={() => handleYearClick(year)}
              className={`
                px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors duration-200
                ${activeYears.includes(year) ? "bg-orange-500 text-white shadow-md" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}
              `}
              style={{
                minWidth: `${BUTTON_WIDTH}px`,
                userSelect: "none",
              }}
            >
              {year}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CompatibilityScrollableHeader;
